import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connectRange } from 'react-instantsearch-dom'
import { Input, Box, Level, Accordion, Icon, Text, RadioGroup } from '@deseretbook/react-ui'
import { Analytics } from '../../../../dbcom'
import { trackAlgoliaEvent } from '../analytics'

/**
 * Container for the Min/Max range components
 *
 * This component is setup to use the Price range for products
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/range-input/react/
 *
 * @param {object} currentRefinement - Contains min/max values
 * @param {number} min - The minimum Price value
 * @param {number} max - The maximum Price Value
 * @param {function} refine - Updates the current min/max values
 * @param {Array} count - Updates the current min/max values
 * @param {boolean} isOpenByDefault - Whether to show the priceRange by default
 * @param {string} attribute - The attribute name
 *
 * @returns {function} React component
 */
const PriceRangeComponent = ({
  currentRefinement,
  min,
  max,
  refine,
  isOpenByDefault = false,
  attribute,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault)
  const [range, setRange] = useState(currentRefinement)

  /**
   * Updates the current min/max values for the price range and
   * updates the current radio value.
   *
   * @param {object} event - Input event object
   */
  const handleChange = (event) => {
    Analytics.event(
      {
        category: 'Algolia Filter',
        action: 'User applied new price filter to search',
        label: `Applied ${event.currentTarget.value} filter.`,
      },
      { isInteractive: true },
    )

    trackAlgoliaEvent('clickedFilters', {
      eventName: 'Filter Clicked',
      filters: [`${attribute}:${currentRefinement.min}`, `${attribute}:${currentRefinement.max}`],
    })

    const { name = '', value = '' } = event.currentTarget

    let newMin = currentRefinement.min
    let newMax = currentRefinement.max

    switch (name) {
      case 'minimum':
        newMin = value
        break
      case 'maximum':
        newMax = value
        break
      case 'radioPrice':
        // Keep empty strings in destructuring to keep min/max values in correct order
        // eslint-disable-next-line no-case-declarations
        const [minValue, maxValue] = value.split(':')

        newMin = minValue
        newMax = maxValue
        break
      default:
        break
    }

    // Undefined values are used to show no limit
    // Ex. min = 100 and max = undefined it'd return all results with min price 100
    newMin = newMin ? Number(newMin) : undefined
    newMax = newMax ? Number(newMax) : undefined

    setRange({ min: newMin, max: newMax })

    refine({ min: newMin, max: newMax })
  }

  const radioOptions = [
    {
      value: ':0',
      label: 'Free',
      disabled: min > 0,
    },
    {
      value: '0:10',
      label: 'Under $10',
      disabled: min > 10,
    },
    {
      value: '10:25',
      label: '$10 - $25',
      disabled: min > 25 || max < 10,
    },
    {
      value: '25:50',
      label: '$25 - $50',
      disabled: min > 50 || max < 25,
    },
    {
      value: '50:100',
      label: '$50 - $100',
      disabled: min > 100 || max < 50,
    },
    {
      value: '100:',
      label: 'Above $100',
      disabled: max < 100,
    },
    {
      value: ':',
      label: 'Any Price',
    },
  ]

  /**
   * Handles/Tracks Accordion toggle event
   */
  const handleToggle = () => {
    Analytics.event(
      {
        category: 'Algolia Filter',
        action: `User ${isOpen ? 'opened' : 'closed'} the Price Range menu.`,
      },
      {
        isInteractive: true,
      },
    )

    setIsOpen(!isOpen)
  }

  // Resets the range state if its cleared
  useEffect(() => {
    if (currentRefinement.min === min && currentRefinement.max === max) {
      setRange({ min: undefined, max: undefined })
    }
  }, [currentRefinement])

  return (
    <Accordion.Section isOpen={isOpen} onToggle={handleToggle}>
      <Accordion.Section.Header>
        <Box paddingVertical={0.5}>
          <Level>
            <Level.Left>
              <Level.Item>
                <Text style={{ textTransform: 'uppercase' }}>Price</Text>
              </Level.Item>
            </Level.Left>
            <Level.Right>
              <Level.Item>
                <Icon name={`chevron${isOpen ? 'Up' : 'Down'}`} />
              </Level.Item>
            </Level.Right>
          </Level>
        </Box>
      </Accordion.Section.Header>
      <Accordion.Section.Content>
        <Box paddingTop={1} isFullwidth>
          <Level>
            <Level.Item>
              <Input
                type="number"
                name="minimum"
                label="Min"
                value={range.min}
                removeHint
                style={{ padding: '0.5rem' }}
                onChange={handleChange}
                disabled={min === undefined}
              />
            </Level.Item>
            <Level.Item>
              <Input
                type="number"
                name="maximum"
                label="Max"
                removeHint
                style={{ padding: '0.5rem' }}
                value={range.max}
                onChange={handleChange}
                disabled={max === undefined}
              />
            </Level.Item>
          </Level>
        </Box>
        <Box paddingTop={2} isFullwidth>
          <RadioGroup
            name="radioPrice"
            aria-label="pricing options"
            selectedValue={Object.values(range).join(':')}
            onChange={handleChange}
            disabled={min === undefined || max === undefined}
          >
            {radioOptions.map(({ value, disabled = false, label }) => (
              <RadioGroup.Option
                key={value}
                value={value}
                inputProps={{ disabled }}
                labelProps={{
                  isDisabled: disabled || min === undefined || max === undefined,
                }}
              >
                <Level>
                  <Level.Left>
                    <Level.Item>{label}</Level.Item>
                  </Level.Left>
                </Level>
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        </Box>
      </Accordion.Section.Content>
    </Accordion.Section>
  )
}

PriceRangeComponent.propTypes = {
  currentRefinement: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  min: PropTypes.number,
  max: PropTypes.number,
  refine: PropTypes.func,
  isOpenByDefault: PropTypes.bool,
  attribute: PropTypes.string,
}

export const PriceRange = connectRange(PriceRangeComponent)
